import React, { useState, useEffect } from 'react';
import { DhcpIP, dhcpIpApi, IPRange } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import './DhcpIPList.css';

interface DhcpIPListProps {
  ipRange: IPRange;
  searchQuery?: string;
}

const DhcpIPList: React.FC<DhcpIPListProps> = ({ ipRange, searchQuery = '' }) => {
  const { canRelease } = useAuth();
  const [dhcpIPs, setDhcpIPs] = useState<DhcpIP[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [releaseConfirm, setReleaseConfirm] = useState<{ isOpen: boolean; device: DhcpIP | null }>({
    isOpen: false,
    device: null
  });

  useEffect(() => {
    loadDhcpIPs();
  }, [ipRange]);

  const loadDhcpIPs = async () => {
    try {
      setLoading(true);
      const response = await dhcpIpApi.getAll(ipRange.network, ipRange.subnet_mask);
      setDhcpIPs(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load DHCP IPs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = (device: DhcpIP) => {
    setReleaseConfirm({ isOpen: true, device });
  };

  const confirmRelease = async () => {
    if (!releaseConfirm.device) return;
    
    const deviceName = releaseConfirm.device.hostname || releaseConfirm.device.mac_address;
    
    try {
      await dhcpIpApi.release(
        releaseConfirm.device.mac_address || '', 
        releaseConfirm.device.ip_address
      );
      toast.success(`Successfully released DHCP device: ${deviceName}`);
      // Reload the list after successful release
      await loadDhcpIPs();
      setReleaseConfirm({ isOpen: false, device: null });
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to release DHCP device');
      console.error('Error releasing DHCP device:', error);
      setReleaseConfirm({ isOpen: false, device: null });
    }
  };

  const formatDateTime = (dateStr?: string): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ko-KR');
  };

  const getDeviceTypeIcon = (type?: string): string => {
    if (!type) return '💻';
    const lowerType = type.toLowerCase();
    if (lowerType.includes('server')) return '🖥️';
    if (lowerType.includes('printer')) return '🖨️';
    if (lowerType.includes('phone')) return '📱';
    if (lowerType.includes('router') || lowerType.includes('switch')) return '🔌';
    if (lowerType.includes('camera')) return '📷';
    return '💻';
  };

  const filteredIPs = dhcpIPs.filter((ip) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    
    // Check regular fields
    if (ip.ip_address.includes(query) ||
        ip.hostname?.toLowerCase().includes(query) ||
        ip.description?.toLowerCase().includes(query)) {
      return true;
    }
    
    // Special handling for MAC address - normalize both query and stored MAC
    if (ip.mac_address) {
      const normalizedQuery = query.toUpperCase().replace(/[^0-9A-F]/g, '');
      const normalizedMac = ip.mac_address.toUpperCase().replace(/[^0-9A-F]/g, '');
      return normalizedMac.includes(normalizedQuery);
    }
    
    return false;
  });

  return (
    <div className="dhcp-ip-list">
      <div className="dhcp-ip-header">
        <h3>DHCP IP Assignments</h3>
        <span className="dhcp-count">{filteredIPs.length} devices</span>
      </div>

      <div className="ip-range-info">
        <span>Range: {ipRange.network}/{ipRange.subnet_mask}</span>
        {ipRange.dhcp_start && ipRange.dhcp_end && (
          <span>DHCP Pool: {ipRange.dhcp_start} - {ipRange.dhcp_end}</span>
        )}
      </div>

      {loading && <div className="loading">Loading DHCP IPs...</div>}
      {error && <div className="error">{error}</div>}

      <div className="dhcp-ip-table">
        <table>
          <thead>
            <tr>
              <th>IP Address</th>
              <th>Hostname</th>
              <th>MAC Address</th>
              {canRelease() && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filteredIPs.map((ip) => (
              <tr key={ip.id}>
                <td className="ip-address">{ip.ip_address}</td>
                <td>{ip.hostname || '-'}</td>
                <td className="mac-address">{ip.mac_address || '-'}</td>
                {canRelease() && (
                  <td>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleRelease(ip)}
                      disabled={!ip.mac_address}
                      title="Release DHCP Assignment"
                    >
                      Release
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {filteredIPs.length === 0 && !loading && (
              <tr>
                <td colSpan={canRelease() ? 4 : 3} className="empty-message">
                  No DHCP-assigned IPs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={releaseConfirm.isOpen}
        title="Release DHCP Assignment"
        message={`Are you sure you want to release DHCP assignment for "${releaseConfirm.device?.hostname || releaseConfirm.device?.mac_address}"?\n\nIP Address: ${releaseConfirm.device?.ip_address}\nMAC Address: ${releaseConfirm.device?.mac_address || 'N/A'}\n\nThis will delete the device from DHCP database.`}
        confirmText="Release"
        cancelText="Cancel"
        onConfirm={confirmRelease}
        onCancel={() => setReleaseConfirm({ isOpen: false, device: null })}
        variant="warning"
      />
    </div>
  );
};

export default DhcpIPList;